import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2, Upload } from 'lucide-react';
import { getAddress } from 'viem';
import {
  ContractAbiDetailsErrorCode,
  type NetworkType,
  type PolicyFunction,
} from '@mls-wallet/shared-types';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { useContractAbi } from '@/hooks/transaction/use-contract-abi';
import { cn, formatAddress } from '@/lib/utils';
import { collectAddressParameterValues } from './parameter-inputs/collect-address-parameter-values';
import { useManualTransactionAddressValidation } from './useManualTransactionAddressValidation';
import {
  canImportUndecodedRawTransaction,
  decodeContractTransactionImport,
  isCalldataFunctionAllowed,
  normalizeRawContractTransaction,
  parseRawContractTransactionJson,
  type RawContractTransactionImport,
  type RawContractTransaction,
} from './raw-transaction-import';

const MAX_FILE_SIZE_BYTES = 1024 * 1024;
const EMPTY_MANUAL_TRANSACTION: RawContractTransaction = {
  to: '',
  value: '',
  data: '',
};

type InputMode = 'upload' | 'manual';

type RawTransactionImportDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  network: NetworkType | undefined;
  // The importer only distinguishes a fixed policy target from no restriction.
  policyAllowedContractAddress: string | null;
  policyAllowedFunctions: PolicyFunction[];
  policyAllowsUndecodedCalldata: boolean;
  onImportTransaction: (
    payload: RawContractTransactionImport,
    transaction: RawContractTransaction,
    importSource?: 'json-file'
  ) => void;
};

/**
 * Imports the minimal raw EVM transaction shape and decodes it when
 * authoritative ABI metadata recognizes the selector. Calls may instead remain
 * opaque when imported from a JSON file and the policy has no function rules.
 * Manual entry requires decoding so address-typed arguments can be checked.
 */
export function RawTransactionImportDialog({
  open,
  onOpenChange,
  network,
  policyAllowedContractAddress,
  policyAllowedFunctions,
  policyAllowsUndecodedCalldata,
  onImportTransaction,
}: RawTransactionImportDialogProps): React.ReactElement {
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Prevent an older read, including one finishing after close, from replacing
  // newer or reset state.
  const fileReadIdRef = useRef(0);
  const [inputMode, setInputMode] = useState<InputMode>('upload');
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState<string | null>(null);
  const [uploadedTransaction, setUploadedTransaction] =
    useState<RawContractTransaction | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [manualTransaction, setManualTransaction] =
    useState<RawContractTransaction>(EMPTY_MANUAL_TRANSACTION);

  // Wait for all required fields before surfacing validation errors while the
  // user is typing.
  const manualInputResult = useMemo(() => {
    if (Object.values(manualTransaction).some((value) => !value.trim())) {
      return { transaction: null, error: null };
    }

    try {
      return {
        transaction: normalizeRawContractTransaction(manualTransaction),
        error: null,
      };
    } catch (error) {
      return {
        transaction: null,
        error:
          error instanceof Error
            ? error.message
            : 'The transaction fields are invalid.',
      };
    }
  }, [manualTransaction]);

  // Use only the currently selected input method for ABI lookup and validation.
  // Keep the other method's draft when switching between upload and manual entry.
  const transaction =
    inputMode === 'upload'
      ? uploadedTransaction
      : manualInputResult.transaction;
  const inputError =
    inputMode === 'upload' ? fileError : manualInputResult.error;

  // A null key keeps ABI discovery idle until the dialog has a locally valid
  // transaction and network.
  const {
    contractDisplayName,
    implementationFunctions,
    proxyFunctions,
    isLoading,
    errorMessage: abiErrorMessage,
    errorCode: abiErrorCode,
    error: abiNetworkError,
  } = useContractAbi(
    open && transaction && network
      ? { address: transaction.to, networkType: network }
      : null
  );
  const hasAbiLookupError = Boolean(abiErrorMessage || abiNetworkError);

  // Reset both input methods whenever the dialog closes so the next opening
  // starts clean. Advancing the read id also invalidates an in-flight file read.
  useEffect(() => {
    if (open) return;

    fileReadIdRef.current += 1;
    setInputMode('upload');
    setIsDragging(false);
    setFileName(null);
    setUploadedTransaction(null);
    setFileError(null);
    setManualTransaction(EMPTY_MANUAL_TRANSACTION);
  }, [open]);

  // The importer trusts only to/value/data, even when a Safe-style export also
  // contains function metadata. The ABI fetched by the backend is authoritative.
  const decodeResult = useMemo(() => {
    if (!transaction || !contractDisplayName) return null;

    return decodeContractTransactionImport({
      transaction,
      contractName: contractDisplayName,
      implementationFunctions,
      proxyFunctions,
    });
  }, [
    contractDisplayName,
    implementationFunctions,
    proxyFunctions,
    transaction,
  ]);
  const decodedImport =
    decodeResult?.status === 'decoded' ? decodeResult.details : null;

  const importAddresses = useMemo(
    () =>
      transaction
        ? [
            transaction.to,
            ...(decodedImport
              ? collectAddressParameterValues(
                  decodedImport.selectedFunction.inputs,
                  decodedImport.functionParameters
                )
              : []),
          ]
        : [],
    [transaction, decodedImport]
  );
  // File imports retain their supplied addresses. Pasted fields require saved
  // targets and decoded address arguments, just like the manual builder.
  const addressValidation = useManualTransactionAddressValidation(
    importAddresses,
    inputMode === 'upload' ? 'json-file' : undefined
  );

  // An unrestricted policy may continue when no ABI exists or when the fetched
  // ABI simply does not publish the calldata selector.
  const isUndecodedImportReady = Boolean(
    inputMode === 'upload' &&
      transaction &&
      !isLoading &&
      canImportUndecodedRawTransaction({
        abiErrorCode,
        hasAbiLookupError,
        decodeResult,
        policyAllowsUndecodedCalldata,
      })
  );

  // Validation order is intentional: policy and ABI failures win before
  // function-specific checks.
  const validationError = useMemo(() => {
    // Case: neither input method has produced a locally valid transaction yet.
    if (!transaction) return null;

    // Case: decoding cannot start until the builder has a selected network.
    if (!network) return 'Select a network before importing a transaction.';

    // Case: a fixed-contract policy rejects a different target or bad policy data.
    if (policyAllowedContractAddress) {
      try {
        if (
          getAddress(policyAllowedContractAddress) !==
          getAddress(transaction.to)
        ) {
          return 'The imported contract is not allowed by the selected policy.';
        }
      } catch {
        return 'The selected policy contains an invalid contract address.';
      }
    }

    // Case: function-restricted policies cannot accept either form of opaque call.
    if (
      (abiErrorCode === ContractAbiDetailsErrorCode.UNVERIFIED_CONTRACT ||
        decodeResult?.status === 'selector-not-found') &&
      !policyAllowsUndecodedCalldata
    ) {
      return 'The selected policy has function restrictions, so this calldata must be decoded before it can be imported.';
    }

    // Pasted opaque calldata could hide unsaved address arguments. Only an
    // uploaded JSON file may use the existing undecoded import path.
    if (
      inputMode === 'manual' &&
      (abiErrorCode === ContractAbiDetailsErrorCode.UNVERIFIED_CONTRACT ||
        decodeResult?.status === 'selector-not-found')
    ) {
      return 'The calldata must be decoded to check manually entered addresses. Upload a JSON file to import an undecoded transaction.';
    }

    // Case: lookup failures other than an unverified contract still block import.
    if (hasAbiLookupError && !isUndecodedImportReady) {
      return (
        abiErrorMessage ??
        'The contract ABI could not be loaded, so the calldata cannot be decoded.'
      );
    }

    // Case: the selector exists, so malformed arguments must not use the opaque path.
    if (!isLoading && decodeResult?.status === 'arguments-invalid') {
      return 'The calldata arguments do not match the available contract ABI.';
    }

    // Case: a nonpayable function cannot receive native-token value.
    if (
      decodedImport?.selectedFunction.stateMutability === 'nonpayable' &&
      BigInt(transaction.value) > 0n
    ) {
      return `The ${decodedImport.selectedFunction.name} function is nonpayable, so value must be "0".`;
    }

    // Case: policy restrictions are selector-based, so validate raw calldata
    // rather than the decoded display name.
    if (
      decodedImport &&
      !isCalldataFunctionAllowed(transaction, policyAllowedFunctions)
    ) {
      return `The ${decodedImport.selectedFunction.name} function is not allowed by the selected policy.`;
    }

    return addressValidation.errorMessage;
  }, [
    addressValidation.errorMessage,
    inputMode,
    abiErrorMessage,
    abiErrorCode,
    decodeResult,
    decodedImport,
    hasAbiLookupError,
    isLoading,
    isUndecodedImportReady,
    network,
    policyAllowedContractAddress,
    policyAllowedFunctions,
    policyAllowsUndecodedCalldata,
    transaction,
  ]);

  const handleFile = async (file: File) => {
    // Starting a new selection immediately invalidates any slower read from
    // the previous file.
    const fileReadId = ++fileReadIdRef.current;
    setFileError(null);
    setUploadedTransaction(null);
    setFileName(file.name);

    if (!file.name.toLowerCase().endsWith('.json')) {
      setFileError('Choose a .json file.');
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setFileError('The JSON file must be smaller than 1 MB.');
      return;
    }

    try {
      const contents = await file.text();
      if (fileReadId !== fileReadIdRef.current) return;

      // Safe-style files carry a chainId; the minimal {to, value, data} shape
      // does not.
      const parsedTransaction = parseRawContractTransactionJson(
        contents,
        network
      );
      setUploadedTransaction(parsedTransaction);
    } catch (error) {
      if (fileReadId !== fileReadIdRef.current) return;

      setFileError(
        error instanceof Error
          ? error.message
          : 'The transaction JSON could not be read.'
      );
    }
  };

  const importPayload = useMemo<RawContractTransactionImport | null>(() => {
    if (decodedImport) {
      return { status: 'decoded', details: decodedImport };
    }
    if (transaction && isUndecodedImportReady) {
      return {
        status: 'undecoded',
        contractAddress: transaction.to,
      };
    }
    return null;
  }, [decodedImport, isUndecodedImportReady, transaction]);

  const canImport = Boolean(
    transaction &&
      importPayload &&
      !validationError &&
      !isLoading &&
      addressValidation.isValid
  );

  const completeImport = () => {
    if (!canImport || !transaction || !importPayload) return;
    // The payload fills the editable form; the normalized raw fields remain
    // its submission source whether or not ABI metadata was available.
    onImportTransaction(
      importPayload,
      transaction,
      inputMode === 'upload' ? 'json-file' : undefined
    );
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>Import custom transaction</DialogTitle>
          <DialogDescription>
            Upload a JSON file or enter raw transaction fields to review the
            contract call before importing it.
          </DialogDescription>
        </DialogHeader>

        <Tabs
          value={inputMode}
          onValueChange={(value) => {
            if (value !== 'upload' && value !== 'manual') return;
            setInputMode(value);
            setIsDragging(false);
          }}
          className="w-full"
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="upload">Upload JSON</TabsTrigger>
            <TabsTrigger value="manual">Enter manually</TabsTrigger>
          </TabsList>

          <TabsContent value="upload" className="mt-4 space-y-4">
            <button
              type="button"
              className={cn(
                'flex min-h-[150px] w-full flex-col items-center justify-center gap-3 rounded-lg border border-dashed bg-zinc-50 px-6 py-5 text-center transition-colors hover:bg-zinc-100',
                isDragging && 'border-zinc-500 bg-zinc-100'
              )}
              onClick={() => fileInputRef.current?.click()}
              onDragEnter={(event) => {
                event.preventDefault();
                setIsDragging(true);
              }}
              onDragOver={(event) => event.preventDefault()}
              onDragLeave={(event) => {
                event.preventDefault();
                setIsDragging(false);
              }}
              onDrop={(event) => {
                event.preventDefault();
                setIsDragging(false);
                const file = event.dataTransfer.files[0];
                if (file) void handleFile(file);
              }}
            >
              <Upload className="size-5 text-zinc-600" />
              <div>
                <p className="text-sm font-medium text-zinc-900">
                  {fileName ?? 'Drop a JSON file here'}
                </p>
                <p className="mt-1 text-xs text-zinc-500">
                  or click to choose a file
                </p>
              </div>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json,application/json"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void handleFile(file);
                // Let the same file trigger onChange again after it is corrected in place.
                event.target.value = '';
              }}
            />

            <div>
              <p className="text-xs font-medium text-zinc-600">
                Supported JSON formats
              </p>
              <div className="mt-2 grid gap-3 sm:grid-cols-2">
                <div className="rounded-lg border bg-zinc-50 p-3">
                  <p className="text-xs font-medium text-zinc-700">
                    Raw transaction
                  </p>
                  <pre className="mt-2 overflow-x-auto text-xs text-zinc-700">
                    {`{
  "to": "0x…",
  "value": "0",
  "data": "0x…"
}`}
                  </pre>
                </div>
                <div className="rounded-lg border bg-zinc-50 p-3">
                  <p className="text-xs font-medium text-zinc-700">
                    Transaction Builder
                  </p>
                  <pre className="mt-2 overflow-x-auto text-xs text-zinc-700">
                    {`{
  "chainId": "${network ?? 1}",
  "transactions": [{
    "to": "0x…",
    "value": "0",
    "data": "0x…"
  }]
}`}
                  </pre>
                </div>
              </div>
              <p className="mt-2 text-xs text-zinc-500">
                value must be a base-unit integer string (wei). data must
                include the 4-byte function selector.
              </p>
            </div>
          </TabsContent>

          <TabsContent value="manual" className="mt-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="raw-transaction-to">To</Label>
              <Input
                id="raw-transaction-to"
                value={manualTransaction.to}
                onChange={(event) =>
                  setManualTransaction((current) => ({
                    ...current,
                    to: event.target.value,
                  }))
                }
                placeholder="0x…"
                autoComplete="off"
                spellCheck={false}
                className="font-mono text-sm"
              />
              <p className="text-xs text-zinc-500">
                The contract address that will receive the call.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="raw-transaction-value">Value</Label>
              <Input
                id="raw-transaction-value"
                type="text"
                inputMode="numeric"
                value={manualTransaction.value}
                onChange={(event) =>
                  setManualTransaction((current) => ({
                    ...current,
                    value: event.target.value,
                  }))
                }
                placeholder="0"
                autoComplete="off"
                className="font-mono text-sm"
              />
              <p className="text-xs text-zinc-500">
                A non-negative base-unit integer string (wei).
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="raw-transaction-data">Data</Label>
              <Textarea
                id="raw-transaction-data"
                value={manualTransaction.data}
                onChange={(event) =>
                  setManualTransaction((current) => ({
                    ...current,
                    data: event.target.value,
                  }))
                }
                placeholder="0x…"
                autoComplete="off"
                spellCheck={false}
                className="min-h-28 resize-y font-mono text-sm"
              />
              <p className="text-xs text-zinc-500">
                0x-prefixed calldata including the 4-byte function selector.
              </p>
            </div>
          </TabsContent>
        </Tabs>

        {(inputError || validationError) && (
          <div className="flex gap-2 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertCircle className="mt-0.5 size-4 shrink-0" />
            <p>{inputError ?? validationError}</p>
          </div>
        )}

        {transaction && addressValidation.isLoading && !validationError && (
          <p role="status" className="text-sm text-zinc-600">
            Checking addresses in the address book…
          </p>
        )}

        {transaction && isLoading && !validationError && (
          <div className="flex items-center gap-2 rounded-lg border p-3 text-sm text-zinc-600">
            <Loader2 className="size-4 animate-spin" />
            Decoding calldata with the contract ABI…
          </div>
        )}

        {transaction &&
          decodedImport &&
          !validationError &&
          addressValidation.isValid && (
            <div className="rounded-lg border p-3 text-sm">
              <div className="flex items-center gap-2 font-medium text-zinc-900">
                <CheckCircle2 className="size-4 text-emerald-600" />
                Ready to import
              </div>
              <dl className="mt-3 grid grid-cols-[90px_1fr] gap-x-3 gap-y-2 text-xs">
                <dt className="text-zinc-500">Contract</dt>
                <dd className="truncate text-zinc-800">
                  {decodedImport.contractName} ({formatAddress(transaction.to)})
                </dd>
                <dt className="text-zinc-500">Function</dt>
                <dd className="text-zinc-800">
                  {decodedImport.selectedFunction.name}
                </dd>
                <dt className="text-zinc-500">Arguments</dt>
                <dd className="text-zinc-800">
                  {decodedImport.functionParameters.length}
                </dd>
                <dt className="text-zinc-500">Value</dt>
                <dd className="break-all font-mono text-zinc-800">
                  {transaction.value} wei
                </dd>
              </dl>
            </div>
          )}

        {transaction &&
          isUndecodedImportReady &&
          !validationError &&
          addressValidation.isValid && (
            <div className="rounded-lg border p-3 text-sm">
              <div className="flex items-center gap-2 font-medium text-zinc-900">
                <CheckCircle2 className="size-4 text-emerald-600" />
                Ready to import
              </div>
              <dl className="mt-3 grid grid-cols-[90px_1fr] gap-x-3 gap-y-2 text-xs">
                <dt className="text-zinc-500">Contract</dt>
                <dd className="font-mono text-zinc-800">
                  {formatAddress(transaction.to)}
                </dd>
                <dt className="text-zinc-500">Value</dt>
                <dd className="break-all font-mono text-zinc-800">
                  {transaction.value} wei
                </dd>
              </dl>
            </div>
          )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!canImport} onClick={completeImport}>
            Import transaction
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
